<template>
  <div>
    <div class="t-table-header-sub">
      <c-select-group class="filter-selection">
        <c-select
          :value="filterOptions[0].value"
          :options="filterOptions"
          @input="onFilterChange"
          qe-id="table-filter"
        />
      </c-select-group>
      <c-search-input
        @on-change="onSearchTerm"
        :placeholder="$t('table.search.input_placeholder')"
        data-qe-id="table-search-input"
      />
    </div>
    <c-overview-table
      v-if="currentFilter === TableFilter.DEFAULT"
      :data="seasonRankingsRange"
      :isSearchActive="isSearchActive"
      :lastQualifiedRank="lastQualifiedRank"
      :maxRows="maxRows"
      :myRank="myRank"
      @show-more="onLoadMore"
      :data-qe-id="`table--${TableFilter.DEFAULT}`"
    />
    <c-score-table
      v-if="currentFilter === TableFilter.SCORES"
      :data="scoreUsersRange"
      :isSearchActive="isSearchActive"
      :lastQualifiedRank="lastQualifiedRank"
      :maxRows="maxRows"
      :myRank="myRank"
      @show-more="onLoadMore"
      :data-qe-id="`table--${TableFilter.SCORES}`"
    />
    <c-ticket-table
      v-if="currentFilter === TableFilter.TICKETS"
      :data="ticketUsersRange"
      :isSearchActive="isSearchActive"
      :maxRows="maxRows"
      :myRank="myRank"
      @show-more="onLoadMore"
      :data-qe-id="`table--${TableFilter.TICKETS}`"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import CScoreTable from '@/components/ranking/season/CScoreTable.vue'
import CSearchInput from '@/components/CSearchInput.vue'
import CSelectGroup from '@/components/CSelectGroup.vue'
import CSelect from '@/components/CSelect.vue'
import COverviewTable from '@/components/ranking/season/COverviewTable.vue'
import CTicketTable from '@/components/ranking/season/CTicketTable.vue'
import { SeasonRanking } from '@/model/ranking/Season'
import { State } from '@/store/state'
import { TableFilter as _TableFilter } from '@/model/ranking/TableFilter'
import { jslog } from '@gd/jslog'
import { track } from '@/lib/track'

const props = defineProps<{ zoneId: string }>()
const route = useRoute()
const store = useStore()
const { t } = useI18n()

const rowsToRender = ref(0)
const currentPage = ref(0)
const TableFilter = _TableFilter
const ROWS_PER_BATCH = {
  [TableFilter.DEFAULT]: 50,
  [TableFilter.SCORES]: 50,
  [TableFilter.TICKETS]: 15,
}

const currentFilter: Ref<_TableFilter> = ref(TableFilter.DEFAULT)

const searchTerm = ref('')

const rankings = computed((): Array<SeasonRanking> => {
  return store.getters['ranking/season/rankings']()
})

const myRank = computed((): SeasonRanking => {
  return store.getters['ranking/season/myRankUi']
})

const lastQualifiedRank = computed((): number => {
  const rank = store.state.ranking.season.current.lastQualifiedRank
  return rank
})

const maxRows = computed((): number => {
  switch (currentFilter.value) {
    case TableFilter.SCORES:
      return isSearchActive.value
        ? scoreUsersRange.value.length
        : rankings.value.filter((item) => !item.ticketInfo).length
    case TableFilter.TICKETS:
      return isSearchActive.value
        ? ticketUsersRange.value.length
        : Math.floor(
            rankings.value.filter((item) => item.ticketInfo).length /
              (store.state as State).tournament.metaData.numberOfTickets
          )
    default:
      return isSearchActive.value ? seasonRankingsRange.value.length : rankings.value.length
  }
})

/**
 * Get amount of rows depending on selected filter
 */
const rowsPerLoadingBatch = computed((): number => {
  return ROWS_PER_BATCH[currentFilter.value]
})

/**
 * Consider search as active when minimum amount of characters is reached
 */
const isSearchActive = computed(() => {
  return searchTerm.value.length > 0
})

const filterOptions = computed(() => {
  return [
    { name: t('table.filters.masters_no_filter'), value: TableFilter.DEFAULT },
    { name: t('table.filters.masters_score_only'), value: TableFilter.SCORES },
    { name: t('table.filters.masters_tickets_only'), value: TableFilter.TICKETS },
  ]
})

function created() {
  store.dispatch('ranking/season/fetch', props.zoneId).catch((err) => {
    jslog.send({ name: 'fatal', data: { message: 'could not load season table' } })
    console.error(err)
  })
}

/**
 * Default table data
 */
const seasonRankingsRange = computed((): Array<SeasonRanking> => {
  return store.getters['ranking/season/rankings'](0, rowsToRender.value, searchTerm.value)
})

/**
 * Table data filtered for users who qualified by score
 */
const scoreUsersRange = computed((): Array<SeasonRanking> => {
  let rankings = store.getters['ranking/season/rankingsHighScoresOnly'](0, rowsToRender.value, searchTerm.value)
  if (rowsToRender.value <= lastQualifiedRank.value) {
    rankings = rankings.filter((ranking: SeasonRanking, index: number, arr: Array<SeasonRanking>) => {
      if (lastQualifiedRank.value > arr.length) {
        return ranking.seasonRank === '-' || (ranking.seasonRank as number) <= lastQualifiedRank.value
      } else {
        return (ranking.seasonRank as number) <= lastQualifiedRank.value
      }
    })
  }
  return rankings
})

/**
 * Table data filtered for users who qualified by ticket
 */
const ticketUsersRange = computed((): Array<SeasonRanking> => {
  return store.getters['ranking/season/rankingsTicketsOnly'](0, rowsToRender.value, searchTerm.value)
})

/**
 * Calculate page index and amount of entries to show initially and after switching table filters
 */
function setupTable(numRows: number) {
  rowsToRender.value = validateRowsAmount(numRows)
  const pageQuotient = rowsToRender.value / ROWS_PER_BATCH[currentFilter.value]
  currentPage.value = Math.ceil(pageQuotient)
  if (currentPage.value / pageQuotient < 1.25) {
    currentPage.value++
  }
}

/**
 * Returns amount of rows up to ranking-list end initially
 * but also returns memorized amount of rows when user switches filter-views back and forth
 */
function validateRowsAmount(rowAmount: number): number {
  if (currentFilter.value !== TableFilter.TICKETS && rowAmount <= lastQualifiedRank.value) {
    return lastQualifiedRank.value
  } else {
    return rowAmount
  }
}

/**
 * Event handling
 */
function onLoadMore() {
  if (rowsPerLoadingBatch.value * currentPage.value <= maxRows.value) {
    rowsToRender.value = rowsPerLoadingBatch.value * currentPage.value
    currentPage.value++

    track({
      id: 'seasonTable',
      elem: 'load-more',
      path: route.fullPath,
    })
  } else {
    rowsToRender.value = maxRows.value
  }
}

function onFilterChange(type: _TableFilter) {
  currentFilter.value = type
  setupTable(rowsToRender.value)
  track({
    id: 'seasonTable',
    elem: 'filter-changed',
    data: type,
    path: route.fullPath,
  })
}

function onSearchTerm(term: string) {
  searchTerm.value = term
}

/**
 * Calculate number of rows to display once lastQualifiedRank is set
 */
watch(
  lastQualifiedRank,
  (to: number) => {
    setupTable(to)
  },
  { immediate: true }
)

created()
</script>

<style lang="less" scoped></style>
